<template>
    <v-card
        v-if="hasRequiredData"
        class="table-current-permissions"
    >
        <!-- Header -->
        <v-row class="table-current-permissions__header">
            <!-- Show all switch -->
            <v-switch
                v-model="models.showAllLocations"
                class="switch-toggle-all-locations"
                title="Show or Hide all Locations"
                label="Show All Locations with Permissions"
                :disabled="!hasCurrentPermissionsList"
            />
            <v-spacer/>
            <ClButton
                class="button-new-location-permissions"
                title="Add Permission to a new Location"
                aria-label="Add Permission to a new Location"
                @click="addNewButtonHandler()"
            >
                <v-icon left>mdi-shield-plus-outline</v-icon>
                New Location Permissions
            </ClButton>
        </v-row>

        <!-- No Permissions Anywhere Text -->
        <v-row v-if="!hasCurrentPermissionsList">
            <p class="no-permissions-text">
                No Current Permissions
            </p>
        </v-row>
        <!-- Permissions Table -->
        <ClTableFilterable
            v-else
            :customFilter="customFilter"
            :expanded.sync="table.expanded"
            :filter.sync="table.filter"
            :headers="table.headers"
            :items="displayedLocationPermissionList"
            :itemsPerPage="-1"
            :sortBy.sync="table.sortBy"
            :sortDesc.sync="table.sortDesc"
            itemKey="friendly"
            filterLabel="Filter Permissions..."
            showExpand
            hideAdd
        >
            <template #[`item.roleList`]="{ item }">
                {{ overviewRoleListString( item.roleList ) }}
            </template>
            <template #[`item.actions`]="{ item }">
                <span class="row__actions">
                    <ClButton
                        :disabled="item.roleList.length === 0"
                        title="Remove All Permissions for Location"
                        aria-label="Remove All Permissions for Location"
                        type="secondary"
                        @click="removeButtonHandler( item )"
                    >
                        <v-icon left>mdi-delete</v-icon>
                        Remove All
                    </ClButton>
                    <ClButton
                        title="Add Permission for Location"
                        aria-label="Add Permission for Location"
                        @click="addButtonHandler( item )"
                    >
                        <v-icon left>mdi-shield-plus</v-icon>
                        Add
                    </ClButton>
                </span>
            </template>
            <template v-slot:expanded-item="{ headers, item }">
                <!-- Custom Detail Rows -->
                <td
                    v-if="item.roleList.length > 0"
                    :colspan="headers.length"
                >
                    <v-row
                        v-for="( role, index ) in item.roleList"
                        :key="`${ item.friendly }${ index }`"
                        class="expanded-row"
                    >
                        <v-col>
                            <p class="expanded-row__role-name">{{ role.friendly }}</p>
                            <p class="expanded-row__actions">{{ roleActionListString( role.actions ) }}</p>
                        </v-col>
                        <ClButton
                            class="expanded-row__button-remove"
                            title="Remove Permission"
                            aria-label="Remove Permission"
                            type="secondary"
                            @click="removeButtonHandler( item, role )"
                        >
                            <v-icon left>mdi-delete</v-icon>
                            Remove
                        </ClButton>
                    </v-row>
                </td>
                <td
                    v-else
                    :colspan="headers.length"
                >
                    <div class="row-no-roles">
                        <p class="row-no-roles__text">No Roles</p>
                    </div>
                </td>
            </template>
        </ClTableFilterable>

    </v-card>
</template>

<script>
// Vuex
import { mapState, mapGetters } from 'vuex';

/**
 * @params actionList { Array } of { Strings }
 * @returns { Array } of { Object }: { id: 'ORIGINAL_NAME', friendly: 'Original Name' }
 */
function transformActionList( actionList ) {
    if ( !actionList ) return actionList;
    return actionList.map( ( action ) => ( {
        id: action,
        friendly: action
            // replace underscores with spaces
            .replace( /_/gm, ' ' )
            // 'Title Case' the string
            .replace(
                /\w\S*/g,
                ( txt ) => txt.charAt( 0 ).toUpperCase() + txt.substr( 1 ).toLowerCase(),
            ),
    } ) );
}
/**
 * @params roleList { Array } of Role { Object }
 * @returns { Array } of sorted Role { Object } with transformed actions
 */
function transformRoleList( roleList ) {
    return !!roleList && roleList.length && roleList.length > 0
        ? roleList
            .map( ( role ) => ( {
                ...role,
                actions: [ ...transformActionList( role.actions ) ]
                    .sort( ( a, b ) => ( a.friendly > b.friendly ? 1 : -1 ) ),
            } ) )
            .sort( ( a, b ) => ( a.friendly > b.friendly ? 1 : -1 ) )
        : [];
}

export default {
    name: 'TableCurrentPermissions',
    props: {
        userEmailAddress: {
            type: String,
            required: true,
        },
        locationList: {
            type: Array,
            required: true,
        },
        currentPermissionsList: {
            type: Object,
            required: true,
        },
    },
    data() {
        return {
            models: {
                showAllLocations: false,
            },
            table: {
                filter: '',
                sortBy: [ 'friendly' ],
                sortDesc: [ false ],
                expanded: [],
                headers: [
                    {
                        text: 'Location',
                        value: 'friendly',
                    },
                    {
                        text: 'Roles',
                        value: 'roleList',
                    },
                    {
                        text: 'Actions',
                        value: 'actions',
                    },
                ],
            },
        };
    },
    computed: {
        ...mapState( {
            websiteInfo: ( state ) => state.Website.info,
        } ),
        ...mapGetters( {
            hasWebsiteInfo: 'Website/hasInfo',
        } ),
        /** @returns { Boolean } */
        hasUserEmailAddress() {
            return !!this.userEmailAddress;
        },
        /** @returns { Boolean } */
        hasLocationList() {
            return this.locationList.length > 0;
        },
        /** @returns { Boolean } */
        hasCurrentPermissionsList() {
            return Object.values( this.currentPermissionsList ).length > 0;
        },
        /** @returns { Boolean } */
        hasRequiredData() {
            return this.hasUserEmailAddress
                && this.hasLocationList;
        },

        /** @returns { Array } */
        rolesForCurrentLocation() {
            return this.hasCurrentPermissionsList
                && this.hasWebsiteInfo
                && !!this.currentPermissionsList[ this.websiteInfo.locationId ]
                ? this.currentPermissionsList[ this.websiteInfo.locationId ].authRoleDTOS
                : [];
        },

        /** @returns { Array } of Locations for which the userEmailAddress has roles */
        formattedLocationPermissionList() {
            return this.locationList
                .filter( ( location ) => (
                    this.currentPermissionsList[ location.id ]
                    && this.currentPermissionsList[ location.id ].authRoleDTOS
                    && this.currentPermissionsList[ location.id ].authRoleDTOS.length
                    && ( this.currentPermissionsList[ location.id ].authRoleDTOS.length > 0 )
                ) )
                .map( ( location ) => ( {
                    ...location,
                    roleList: this.currentPermissionsList[ location.id ]
                        ? transformRoleList( this.currentPermissionsList[ location.id ].authRoleDTOS )
                        : [],
                } ) );
        },
        /** @returns { Array } */
        displayedLocationPermissionList() {
            return this.models.showAllLocations
                ? this.formattedLocationPermissionList
                : [ {
                    id: this.websiteInfo.locationId,
                    friendly: this.websiteInfo.name,
                    roleList: transformRoleList( this.rolesForCurrentLocation ),
                } ];
        },
    },
    watch: {
        hasCurrentPermissionsList() {
            if ( !this.hasCurrentPermissionsList ) this.models.showAllLocations = false;
        },
    },
    methods: {
        customFilter( value, searchString ) {
            switch ( typeof value ) {
                case 'string':
                    return value
                        .toLowerCase()
                        .includes( searchString.toLowerCase() );
                case ( 'object' ):
                    return value
                        .map( ( role ) => role.friendly )
                        .join( ' ' )
                        .toLowerCase()
                        .includes( searchString.toLowerCase() );
                default:
                    return false;
            }
        },
        /**
         * @params roleList { Array } of { Object }
         * Input: [ { id: '101', friendly: 'First Role', ... }, { id: '102', friendly: 'Second Role', ... } ]
         * Output: 'First Role, Second Role'
         * @returns { String }
         */
        overviewRoleListString( roleList ) {
            return !roleList || roleList.length === 0
                ? 'No Roles'
                : roleList.map( ( role ) => role.friendly ).join( ', ' );
        },
        roleActionListString( actionList ) {
            return !actionList
                ? 'No actions setup for this Role'
                : actionList.map( ( action ) => action.friendly ).join( ', ' );
        },

        emitAddRole( { location, type } ) {
            this.$emit( 'addRole', {
                location,
                type,
            } );
        },
        addNewButtonHandler() {
            this.emitAddRole( {
                location: { id: null },
                type: 'NewLocation',
            } );
        },
        addButtonHandler( location ) {
            this.emitAddRole( {
                location,
                type: 'ExistingLocation',
            } );
        },
        async removeButtonHandler( location, role ) {
            let message = '';
            let roles = [];
            if ( !role ) {
                message = `Are you sure you want to remove all roles ${ message }`;
                roles = location.roleList;
            } else {
                message = `Are you sure you want to remove the '${ role.friendly }' role ${ message }`;
                roles = [ role ];
            }
            message += ` from ${ this.userEmailAddress } at ${ location.friendly }?`;

            await this.$store.dispatch( 'dialog', {
                title: roles.length > 1
                    ? 'Remove Roles'
                    : 'Remove Role',
                message,
                cancelText: 'Nevermind',
                confirmText: 'Remove',
                width: '500px',
                onConfirm: () => {
                    this.$emit( 'removeRoles', {
                        location,
                        roles,
                    } );
                },
            } );
        },
    },
};
</script>

<style scoped lang="scss">
.table-current-permissions {
    margin-top: 3rem;
    margin-bottom: 1rem;

    .no-permissions-text {
        margin-left: auto;
        margin-right: auto;
    }

    &__header {
        margin-bottom: 1rem;
        .switch-toggle-all-locations {
            margin-right: auto;
            margin-left: 1.5rem;
        }
        .button-new-location-permissions {
            margin-right: auto;
            margin-left: auto;
            width: 85%;
        }
    }

    .expanded-row {
        margin-top: .5rem;
        border-bottom: 1px solid grey;

        &__role-name {
            font-size: 110%;
            font-weight: bold;
        }
        &__actions {
            font-size: 90%;
            opacity: 90%;
        }
        &__button-remove {
            margin-top: auto;
            margin-right: 1rem;
            margin-bottom: auto;
        }
    }
    .row-no-roles {
        display: flex;
        flex-flow: row nowrap;
        &__text {
            margin: 1rem auto;
        }
    }
}

.row__actions {
    width: 100%;
    display: grid;
    grid-template-columns: repeat(2, min-content);
    column-gap: 1rem;
}

@include mixins-above(tablet) {
    .table-current-permissions {
        &__header {
            margin-bottom: auto;
            .switch-toggle-all-locations {
                margin-left: 2rem;
            }
            .button-new-location-permissions {
                margin-top: 1rem;
                margin-right: 1.75rem;
                width: auto;
            }
        }
    }
}
</style>
